import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext({});
const USER_SESSION_KEY = 'gs_user_session'; // { userId: '123' }

export function AuthProvider({ children }) {
  const [role, setRole] = useState(null);    // 'admin' | 'user' | null
  const [userId, setUserId] = useState(null);
  const [adminName, setAdminName] = useState('');  // display name for the logged-in admin
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Check localStorage for a stored user (member) session first
    const stored = localStorage.getItem(USER_SESSION_KEY);
    if (stored) {
      try {
        const { userId: storedId } = JSON.parse(stored);
        if (storedId) {
          setRole('user');
          setUserId(String(storedId));
          setLoading(false);
          return; // no need to check Supabase session
        }
      } catch { /* corrupt data — ignore */ }
    }

    // 2. Check Supabase session for admin (email/password login)
    supabase.auth.getSession().then(({ data: { session } }) => {
      resolveAdminSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Only handle admin sessions here; user sessions are in localStorage
      if (!localStorage.getItem(USER_SESSION_KEY)) {
        resolveAdminSession(session);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveAdminSession = (session) => {
    if (session?.user?.email) {
      setRole('admin');
      setUserId(null);
      // Prefer display name from metadata, fall back to email
      const name =
        session.user.user_metadata?.full_name ||
        session.user.user_metadata?.name ||
        session.user.email;
      setAdminName(name);
    } else {
      setRole(null);
      setUserId(null);
      setAdminName('');
    }
    setLoading(false);
  };

  /** Called by Login after verifying phone + member ID against users table */
  const loginAsUser = (id) => {
    localStorage.setItem(USER_SESSION_KEY, JSON.stringify({ userId: String(id) }));
    setRole('user');
    setUserId(String(id));
  };

  const signOut = async () => {
    if (role === 'admin') {
      await supabase.auth.signOut();
    } else {
      localStorage.removeItem(USER_SESSION_KEY);
    }
    setRole(null);
    setUserId(null);
  };

  return (
    <AuthContext.Provider value={{ role, userId, adminName, loading, loginAsUser, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
